import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { MockTableData } from '../../../mocks/mock-table-data';
import { NotificationItem, NotificationType } from '../../../models/notification.model';
import { of, firstValueFrom, catchError, Subscription } from 'rxjs';
import { NotificationFilterService } from '../../../shared/services/notification-filter.service';
import { NotificationFilterComponent } from '../../../components/notificationFilter/notification-filter.component';

@Component({
  selector: 'app-tables-main-page',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatIconModule, NotificationFilterComponent],
  templateUrl: './tableMainPage.component.html',
  styleUrls: []
})
export class TableMainPageComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _environment = inject(EnvironmentService);
  private _notifService = inject(NotificationService);
  private _filterService = inject(NotificationFilterService);
  allTables: NotificationItem[] = [];
  loading = signal(true);
  error = '';

  private _pushSub?: Subscription;
  private _filterSub?: Subscription;

  async ngOnInit(): Promise<void> {
    this._filterService.reset();
    this._filterSub = this._filterService.currentFilter$.subscribe(() => {
      void this.loadTables();
    });

    this._pushSub = this._notifService.pushReceived$.subscribe(event => {
      if (event.type === NotificationType.Table) {
        console.log('[TableMainPage] Push received, reloading tables...');
        this.loadTables();
      }
    });
  }

  ngOnDestroy(): void {
    this._pushSub?.unsubscribe();
    this._filterSub?.unsubscribe();
  }
    get filteredTables(): NotificationItem[] {
  const currentFilter = this._filterService?.getCurrentFilter?.() || 'all';
  
  switch (currentFilter) {
    case 'unread':
      return this.allTables?.filter(m => !m.isRead)?.sort((a, b) => 
        new Date(b.created).getTime() - new Date(a.created).getTime()
      ) || this.allTables || [];
    case 'all':
    default:
      return this.allTables?.sort((a, b) => 
        new Date(b.created).getTime() - new Date(a.created).getTime()
      ) || this.allTables || [];
  }
}

  async refreshTables(): Promise<void> {
    await this.loadTables();
  }

  async loadTables(): Promise<void> {
    this.loading.set(true);
    this.error = '';

    console.log('[TablesMainPage] Loading table notifications...');

    const tablesObservable = this._environment.isDesignMode
      ? of(this.loadMockTables())
      : this.loadRealTables();

    const tables = await firstValueFrom(
      tablesObservable.pipe(
        catchError(error => {
          console.error('[TablesMainPage] Error loading tables:', error);
          this.error = 'Error loading tables. Please try again.';
          return of([]);
        })
      )
    ).then(tables => {
      this.allTables = tables;
      console.log(`[MessagesMainPage] Loaded ${this.allTables.length} tables`);
      
      this._filterService.setNotifications(this.allTables);
    }).finally(() => {
      this.loading.set(false);
  });
}

  private loadMockTables(): NotificationItem[] {
    console.log('[TablesMainPage] Using mock data (design mode)');
    return MockTableData.getTableList();
  }

  private loadRealTables() {
    console.log('[TablesMainPage] Using real API data');
    return this._notifService.getNotificationsByType(
      NotificationType.Table,
      this._filterService.getCurrentIsReadFilter()
    );
  }

  onTableClick(tableId: number): void {
    console.log('[TablesMainPage] Clicked table ID:', tableId);

    if (!this.isValidTableId(tableId)) {
      console.error('[TablesMainPage] Invalid table ID:', tableId);
      return;
    }

    const numericId = Number(tableId);
    console.log('[TablesMainPage] Navigating to table detail:', numericId);
    this._router.navigate(['/table/detail', numericId]);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';

    return new Date(dateString).toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private isValidTableId(tableId: number): boolean {
    if (!tableId) {
      return false;
    }

    const numericId = Number(tableId);
    return !isNaN(numericId) && numericId > 0;
  }

  getPreviewText(body: string, maxLength: number = 100): string {
    return this._notifService.getPreviewText(body, maxLength);
  }

  getReadStatusText(isRead: boolean): string {
    return this._notifService.getReadStatusText(isRead);
  }
    get tables(): NotificationItem[] {
    return this.allTables;
  }
}
